import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Form, FormInstance, Input, Select, message } from 'antd'
import { ArrowLeftIcon, PlusIcon } from 'lucide-react'
import { NovelSchema, NovelActSchema, CoreEntityType, CoreEntityMap } from '#/index'
import { useGenerateUUIDByType } from '@/stores/userStore'
import { useNovelCoreStore } from '@/stores/novelCoreStore'
import { useRouter } from '@/router/hooks'
import { MBA_ACT_TITLE, MBA_THESIS_CHAPTERS } from '@/constants/mbaTemplate'
import { useThesisBriefStore } from '@/stores/thesisBriefStore'

type FormData = {
  title: string
  author: string
  researchQuestion: string
  method: string
  language: string
}

type NovelChapter = CoreEntityMap<CoreEntityType.NovelChapters>
type NovelScene = CoreEntityMap<CoreEntityType.NovelScenes>

const initialValues: FormData = {
  title: '',
  author: '',
  researchQuestion: '',
  method: '案例研究',
  language: 'zh_CN',
}

const CreateNovel = () => {
  const formRef = useRef<FormInstance<FormData>>(null)
  const { generateFirstLevelUUID, userId } = useGenerateUUIDByType()
  const { addCoreData, structuralUpdateCoreData } = useNovelCoreStore()
  const router = useRouter()
  const [busy, setBusy] = useState(false)

  const seedMbaOutline = async (novelId: string) => {
    const actId = generateFirstLevelUUID(CoreEntityType.NovelActs)
    const act: NovelActSchema = {
      id: actId,
      type: CoreEntityType.NovelActs,
      attributes: { numerate: false, title: MBA_ACT_TITLE },
      relationships: {
        chapters: [],
        project: { type: 'novels', id: novelId },
      },
      meta: {
        state: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    }
    const actRes = await addCoreData(act)
    if (actRes?.code !== 200) throw new Error('创建目录失败')

    const chapterIds: string[] = []
    for (const ch of MBA_THESIS_CHAPTERS) {
      const chapterId = generateFirstLevelUUID(CoreEntityType.NovelChapters)
      const sceneId = generateFirstLevelUUID(CoreEntityType.NovelScenes)
      const chapter: NovelChapter = {
        id: chapterId,
        type: CoreEntityType.NovelChapters,
        attributes: { numerate: true, title: ch.title },
        relationships: {
          scenes: [sceneId],
          project: { type: 'novels', id: novelId },
        },
        meta: {
          state: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      }
      const scene: NovelScene = {
        id: sceneId,
        type: CoreEntityType.NovelScenes,
        attributes: {
          includeInContext: true,
          // 目录默认只显示章；单节时不占一层「正文草稿」
          title: '',
          summary: ch.goal,
        },
        relationships: {
          labels: [],
          references: [],
          project: { type: 'novels', id: novelId },
        },
        meta: {
          state: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      }
      const [cRes, sRes] = await Promise.all([addCoreData(chapter), addCoreData(scene)])
      if (cRes?.code !== 200 || sRes?.code !== 200) throw new Error('创建章节失败')
      chapterIds.push(chapterId)
    }

    await structuralUpdateCoreData({
      id: actId,
      relationships: { chapters: chapterIds },
    })
    await structuralUpdateCoreData({
      id: novelId,
      relationships: { acts: [actId] },
    })
  }

  const handleCreateNovel = () => {
    if (!formRef.current || busy) return
    const id = generateFirstLevelUUID(CoreEntityType.Novels)
    formRef.current.validateFields().then(async (values) => {
      setBusy(true)
      try {
        const labels = [
          values.researchQuestion
            ? { id: 'rq', label: `研究问题：${values.researchQuestion}` }
            : null,
          values.method ? { id: 'method', label: `方法：${values.method}` } : null,
        ].filter(Boolean) as NovelSchema['attributes']['labels']

        const res = await addCoreData({
          type: CoreEntityType.Novels,
          id,
          meta: {
            state: 'draft',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          attributes: {
            title: values.title,
            author: values.author || '未署名',
            publishedDate: '',
            seriesIndex: '',
            tense: 'present',
            language: values.language || 'zh_CN',
            labels,
            pov: { type: 'thirdPerson' },
          },
          relationships: {
            owner: { type: 'users', id: userId },
            series: '',
            acts: [],
          },
        } as NovelSchema)

        if (res?.code !== 200) {
          message.error('创建失败')
          return
        }

        await seedMbaOutline(id)
        useThesisBriefStore.getState().ensureBrief(id, {
          researchQuestions: values.researchQuestion ? [values.researchQuestion.trim()] : [],
          methodType: values.method || '',
          researchPurpose: values.researchQuestion?.trim()
            ? `回应：${values.researchQuestion.trim()}`
            : '',
        })
        message.success('已创建：六章目录 + 开题卡种子')
        router.push(`/papers/${id}/manuscript?sidebar=thesis-brief`)
      } catch (e) {
        message.error(e instanceof Error ? e.message : '创建失败')
      } finally {
        setBusy(false)
      }
    })
  }

  return (
    <>
      <section className="thesis-hero" style={{ marginBottom: '1.25rem' }}>
        <div>
          <p className="thesis-hero__eyebrow">NEW · MBA</p>
          <h1 className="thesis-hero__title">新建论文</h1>
          <p className="thesis-hero__lead">
            先写清题目与研究问题。创建后自动写入六章目录，可再改标题。
          </p>
        </div>
      </section>

      <div className="thesis-create">
        <div className="thesis-form-panel">
          <Form layout="vertical" ref={formRef} initialValues={initialValues} size="large">
            <Form.Item
              label="论文题目"
              name="title"
              rules={[{ required: true, message: '请输入题目' }]}
            >
              <Input placeholder="例：数字化转型背景下区域零售门店效率提升研究" />
            </Form.Item>
            <Form.Item
              label="核心研究问题"
              name="researchQuestion"
              rules={[{ required: true, message: '请写清研究问题' }]}
              extra="尽量收窄到一个学期内能回答的主问题。"
            >
              <Input.TextArea rows={4} placeholder="你要回答的那个问题是什么？" />
            </Form.Item>
            <Form.Item label="研究方法" name="method">
              <Select
                options={[
                  { value: '案例研究', label: '案例研究' },
                  { value: '问卷调研', label: '问卷调研' },
                  { value: '访谈研究', label: '访谈研究' },
                  { value: '二手数据分析', label: '二手数据分析' },
                  { value: '混合方法', label: '混合方法' },
                ]}
              />
            </Form.Item>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item label="作者" name="author">
                <Input placeholder="你的姓名（可选）" />
              </Form.Item>
              <Form.Item label="语言" name="language">
                <Select
                  options={[
                    { value: 'zh_CN', label: '中文' },
                    { value: 'en_US', label: '英文' },
                  ]}
                />
              </Form.Item>
            </div>
          </Form>
          <div className="thesis-form-actions">
            <Link to="/papers" className="thesis-btn thesis-btn--ghost">
              <ArrowLeftIcon size={18} />
              返回
            </Link>
            <button
              type="button"
              className="thesis-btn thesis-btn--primary"
              disabled={busy}
              onClick={handleCreateNovel}
            >
              <PlusIcon size={18} />
              {busy ? '创建中…' : '创建并生成目录'}
            </button>
          </div>
        </div>

        <aside className="thesis-outline-panel">
          <h3>将生成的目录</h3>
          <p>创建后可在「目录」页改标题与小节。</p>
          <ul className="thesis-outline-list">
            {MBA_THESIS_CHAPTERS.map((ch) => (
              <li key={ch.title}>
                <strong>{ch.title}</strong>
                <span>{ch.goal}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </>
  )
}

export default CreateNovel
